import { priceDateKey } from '@/utils/circuits/prices/priceDateKey';
import StyledButton from '@/components/styled/StyledButton';
import StyledContainer from '@/components/styled/StyledContainer';
import StyledFormHeaderWithBreadcrumb from '@/components/styled/StyledFormHeaderWithBreadcrumb';
import Loader from '@/components/ui/Loader';
import {
  useGetCircuit,
  useUpdateDistributorCircuit,
} from '@/hooks/useCircuits';
import { useLocation, useNavigate, useParams } from 'react-router';
import ArrowLeftIcon from '@/assets/icons/arrow-left.svg?react';
import HorizontalLine from '@/components/ui/HorizontalLine';
import { CircuitDetailsParticipantsCard } from '@/components/distributorCircuits/CircuitDetailsParticipantsCard';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { loadCircuitDefaultValues } from '@/utils/circuits/loadCircuitDefaultValues';
import { CircuitResponse } from '@/types/circuits/CircuitResponse';
import CircuitDetailsSummaryCard from '@/components/distributorCircuits/CircuitDetailsSummaryCard';
import CircuitDetailsPricesCalendarCard from '@/components/distributorCircuits/CircuitDetailsPricesCalendarCard';
import CircuitDetailsItineraryCard from '@/components/distributorCircuits/CircuitDetailsItineraryCard';
import CircuitDetailsImagesCard from '@/components/distributorCircuits/CircuitDetailsImagesCard';
import { getPricingStartDates } from '@/utils/circuits/prices/pricingHorizon';
import { roundUpPrice } from '@/utils/circuits/prices/formatPrice';
import { useCircuitPrices } from '@/hooks/useCircuitPrices';
import { differenceInYears, startOfToday } from 'date-fns';
import EstimateDetailsAndOptions from '@/components/estimates/EstimateDetailsAndOptions';
import { SelectedEstimateOption } from '@/types/estimates/SelectedEstimateOptionType';
import { computeBenefitPrices } from '@/utils/circuits/prices/computeBenefitPrices';
import { BenefitFormData } from '@/types/circuits/CircuitDaysFormData';
import { EstimateStatus } from '@/types/estimates/EstimateStatus';
import { EstimateFormData } from '@/types/estimates/EstimateFormData';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { useConvertPrice } from '@/hooks/useCurrencyExchange';
import { useTranslation } from '@/i18n';
import StyledStepper from '@/components/styled/StyledStepper';
import { useTranslatedConst } from '@/hooks/useTranslatedConst';
import { DISTRIBUTOR_CIRCUIT_STEPS } from '@/constants/steppers/circuitSteps';
import { useToast } from '@/hooks/useToast';
import CreateEditableCircuitModal from '@/components/distributorCircuits/CreateEditableCircuitModal';
import { CircuitFormDataWithTranslations } from '@/i18n/translation-type-utils';
import { SupportedLocale } from '@/types/supported-locale.type';
import { useLanguage } from '@/contexts/LanguageContext';
import { getTranslatedCountry } from '../../../../../shared/CountriesData';
import {
  getCircuitTotalParticipants,
  getEstimateChildrenBirthDatesFromCounts,
} from '@/utils/circuits/participantCounts';
import { buildDistributorCircuitsListSearch } from '@/utils/searchParams/buildDistributorCircuitsListSearch';
import { PATHS } from '@/constants/Paths';
import { formatCircuitCode } from '@/utils/displayCodes';
import {
  CircuitBookingPreviewState,
  getCircuitBookingPreviewKey,
} from '@/types/circuits/CircuitBookingPreview';

const CircuitDetailsPage = () => {
  const { circuitId = '' } = useParams();

  const {
    data: circuitData,
    isLoading,
    isSuccess,
  } = useGetCircuit(circuitId, true);

  return isLoading ? (
    <Loader />
  ) : (
    isSuccess && (
      <CircuitDetails circuitData={circuitData} key={circuitData.id} />
    )
  );
};

export default CircuitDetailsPage;

export const CircuitDetails = ({
  circuitData,
  canEditCircuit = false,
  editableCircuitId,
  canCreateEditableCircuitFromCircuit = true,
}: {
  circuitData: CircuitResponse;
  canEditCircuit?: boolean;
  editableCircuitId?: string;
  canCreateEditableCircuitFromCircuit?: boolean;
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const handleBack = () => {
    const listPath = PATHS.distributors.circuits.list;
    const listSearchParams = location.state?.listSearchParams;
    navigate(listPath + buildDistributorCircuitsListSearch(listSearchParams));
  };
  const { currentLanguage } = useLanguage();

  const parsedCircuit = loadCircuitDefaultValues({
    circuit: circuitData,
    t,
    l: currentLanguage,
  });

  const [currentStep, setCurrentStep] = useState(0);
  const [title, setTitle] = useState(circuitData.name);

  const translateConst = useTranslatedConst();
  const [selectedArrivalDate, setSelectedArrivalDate] = useState<
    Date | undefined
  >(undefined);

  return (
    <StyledContainer>
      <StyledFormHeaderWithBreadcrumb
        title={title}
        code={
          editableCircuitId && circuitData.version != null
            ? `${formatCircuitCode(circuitData.code)}-${circuitData.version}`
            : formatCircuitCode(circuitData.code)
        }
        setTitle={editableCircuitId ? setTitle : undefined}
        breadcrumb={
          <StyledButton
            variant="link"
            title={t('common.backToList')}
            className="text-sm text-[#3D3D3D] pl-0"
            onClick={handleBack}
            icon={<ArrowLeftIcon />}
          />
        }
        subHeading={circuitData.countries
          .map((country) => getTranslatedCountry(country, currentLanguage))
          .join(', ')}
        subTitle={t('circuits.duration', {
          days: circuitData.durationDays,
          nights: circuitData.durationNights,
        })}
      />
      <StyledStepper
        steps={translateConst(DISTRIBUTOR_CIRCUIT_STEPS)}
        activeStep={currentStep}
        setStep={setCurrentStep}
        disableNext={!selectedArrivalDate}
      />
      <HorizontalLine />
      <CircuitDetailsDescription
        parsedCircuit={parsedCircuit as CircuitFormDataWithTranslations}
        circuitId={circuitData.id}
        circuitCurrency={circuitData.currencies[0]}
        selectedArrivalDate={selectedArrivalDate}
        setSelectedArrivalDate={setSelectedArrivalDate}
        isDetailsAndOptions={currentStep === 1}
        onSelectOptionsButtonClick={() => setCurrentStep(1)}
        canEditCircuit={canEditCircuit}
        editableCircuitId={editableCircuitId}
        canCreateEditableCircuitFromCircuit={
          canCreateEditableCircuitFromCircuit
        }
        title={title}
        language={editableCircuitId ? currentLanguage : undefined}
        paragraphStates={circuitData.paragraphStates}
      />
    </StyledContainer>
  );
};

interface CircuitDetailsDescriptionProps {
  circuitId: string;
  parsedCircuit: CircuitFormDataWithTranslations;
  circuitCurrency: string;
  onSelectOptionsButtonClick?: () => void;
  selectedArrivalDate?: Date;
  setSelectedArrivalDate?:
    | React.Dispatch<React.SetStateAction<Date>>
    | React.Dispatch<React.SetStateAction<Date | undefined>>;
  isDetailsAndOptions?: boolean;
  publishCircuit?: () => void;
  errorsLength?: number;
  isValid?: boolean;
  // Editable circuit
  canEditCircuit?: boolean;
  editableCircuitId?: string;
  canCreateEditableCircuitFromCircuit?: boolean;
  title?: string;
  language?: SupportedLocale;
  paragraphStates?: Record<string, string>;
  bookingPreview?: CircuitBookingPreviewState;
}

export const CircuitDetailsDescription = ({
  parsedCircuit,
  circuitId,
  circuitCurrency,
  canEditCircuit = false,
  editableCircuitId,
  onSelectOptionsButtonClick,
  selectedArrivalDate,
  setSelectedArrivalDate,
  isDetailsAndOptions = false,
  publishCircuit,
  errorsLength,
  isValid,
  canCreateEditableCircuitFromCircuit = true,
  title,
  language,
  paragraphStates,
  bookingPreview,
}: CircuitDetailsDescriptionProps) => {
  const { t } = useTranslation();
  const isFieldCustomized = (field: string) => {
    const state = paragraphStates?.[field];
    return state === 'distributor_override' || state === 'receptive_changed';
  };
  const [internalNbAdults, setInternalNbAdults] = useState(
    Number(parsedCircuit.nbParticipants ?? 0),
  );
  const [internalNbChildren, setInternalNbChildren] = useState(
    Number(parsedCircuit.nbChildren ?? 0),
  );
  const [internalNbAdolescents] = useState(
    Number(parsedCircuit.nbAdolescents ?? 0),
  );
  const [internalChildrenBirthDates, setInternalChildrenBirthDates] = useState<
    Record<number, Date>
  >({});
  const [internalSelectedOptions, setInternalSelectedOptions] = useState<
    Record<string, SelectedEstimateOption>
  >({});

  const nbAdults = bookingPreview?.nbAdults ?? internalNbAdults;
  const setNbAdults = bookingPreview?.setNbAdults ?? setInternalNbAdults;
  const nbChildren = bookingPreview?.nbChildren ?? internalNbChildren;
  const setNbChildren = bookingPreview?.setNbChildren ?? setInternalNbChildren;
  const nbAdolescents = bookingPreview?.nbAdolescents ?? internalNbAdolescents;
  const childrenBirthDates =
    bookingPreview?.childrenBirthDates ?? internalChildrenBirthDates;
  const setChildrenBirthDates =
    bookingPreview?.setChildrenBirthDates ?? setInternalChildrenBirthDates;

  const totalParticipants = useMemo(
    () =>
      getCircuitTotalParticipants({
        nbAdults,
        nbChildren,
        nbAdolescents,
      }),
    [nbAdolescents, nbAdults, nbChildren],
  );

  const estimateChildrenBirthDates = useMemo(
    () =>
      getEstimateChildrenBirthDatesFromCounts({
        nbChildren,
        nbAdolescents,
        childrenBirthDates,
        childAgeRanges: parsedCircuit.childAges,
      }),
    [childrenBirthDates, nbAdolescents, nbChildren, parsedCircuit.childAges],
  );

  const childrenAges = useMemo(
    () =>
      estimateChildrenBirthDates.map((birthDate) =>
        differenceInYears(new Date(), birthDate),
      ),
    [estimateChildrenBirthDates],
  );

  const {
    findMinimumPriceForDate,
    daysTrackingMultiDayBenefits,
    lastPricedStartDate,
    ensurePricedUntil,
  } = useCircuitPrices({
    days: parsedCircuit.circuitDays,
    circuitMarginRate: parsedCircuit.marginRate,
    nbParticipants: totalParticipants,
    nbChildren: Number(nbChildren),
    nbAdolescents: Number(nbAdolescents),
    childrenAges,
    targetCurrency: circuitCurrency,
  });

  const parsedCircuitWithMultiDayBenefitTracking = useMemo(
    () => ({ ...parsedCircuit, circuitDays: daysTrackingMultiDayBenefits }),
    [parsedCircuit, daysTrackingMultiDayBenefits],
  );

  const pricesByStartDate = useMemo(
    () =>
      new Map(
        getPricingStartDates().map((date) => [
          date.toISOString(),
          findMinimumPriceForDate(date),
        ]),
      ),
    [findMinimumPriceForDate],
  );

  const totalPrice = useMemo(
    () =>
      roundUpPrice(
        Array.from(pricesByStartDate.values())
          .filter((price) => price > 0)
          .reduce((a, b) => Math.min(a, b), Infinity) /
          (totalParticipants || 1),
      ),
    [pricesByStartDate, totalParticipants],
  );

  const getDayPrice = useCallback(
    (date: Date) =>
      roundUpPrice(
        (pricesByStartDate.get(date.toISOString()) ??
          findMinimumPriceForDate(date)) / totalParticipants,
      ),
    [findMinimumPriceForDate, pricesByStartDate, totalParticipants],
  );

  const isImageString = (image: string | Blob): image is string =>
    !(image instanceof Blob);

  const getTranslationForCircuitValue = useCallback(
    (
      value: keyof Omit<
        Required<(typeof parsedCircuit)['translations']>[SupportedLocale],
        'circuitDays'
      >,
    ) => {
      const parsedValue =
        value === 'modificationConditions' ? 'modificationsConditions' : value;
      if (isFieldCustomized(parsedValue)) {
        return parsedCircuit[parsedValue];
      }
      return language
        ? (parsedCircuit.translations?.[language]?.[value] ??
            parsedCircuit[parsedValue])
        : parsedCircuit[parsedValue];
    },
    [language, parsedCircuit, paragraphStates],
  );

  const daysItinerary = useMemo(
    () =>
      parsedCircuit.circuitDays.map(
        ({ stepTitle, description, images }, index) => ({
          stepTitle:
            isFieldCustomized(`dayTitle_${index}`) || !language
              ? (stepTitle ?? '')
              : (parsedCircuit.translations?.[language]?.circuitDays?.[index]
                  ?.stepTitle ??
                stepTitle ??
                ''),
          description:
            isFieldCustomized(`dayDescription_${index}`) || !language
              ? (description ?? '')
              : (parsedCircuit.translations?.[language]?.circuitDays?.[index]
                  ?.description ??
                description ??
                ''),
          images: (images ?? []).filter(isImageString),
        }),
      ),
    [
      parsedCircuit.circuitDays,
      parsedCircuit.translations,
      language,
      paragraphStates,
    ],
  );

  const images = useMemo(
    () =>
      (parsedCircuit.mainImage
        ? [parsedCircuit.mainImage].concat(parsedCircuit.images)
        : parsedCircuit.images
      ).filter(isImageString),
    [parsedCircuit.mainImage, parsedCircuit.images],
  );

  const isDateSelectable = useCallback(
    (date: Date) => {
      const price = getDayPrice(date);
      return price !== Infinity && price >= 0;
    },
    [getDayPrice],
  );

  const resolvedSelectedOptions =
    bookingPreview?.selectedOptions ?? internalSelectedOptions;
  const resolvedSetSelectedOptions =
    bookingPreview?.setSelectedOptions ?? setInternalSelectedOptions;

  const bookingPreviewKey = useMemo(
    () =>
      getCircuitBookingPreviewKey({
        nbAdults,
        nbChildren,
        nbAdolescents,
        childrenBirthDates,
        selectedArrivalDate,
      }),
    [
      childrenBirthDates,
      nbAdolescents,
      nbAdults,
      nbChildren,
      selectedArrivalDate,
    ],
  );

  const previousBookingPreviewKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (
      previousBookingPreviewKeyRef.current !== null &&
      previousBookingPreviewKeyRef.current !== bookingPreviewKey
    ) {
      resolvedSetSelectedOptions({});
    }
    previousBookingPreviewKeyRef.current = bookingPreviewKey;
  }, [bookingPreviewKey, resolvedSetSelectedOptions]);

  const { convertPrice } = useConvertPrice();

  const computeOptionalBenefitPrice = useCallback(
    (
      benefit: BenefitFormData,
      date: Date,
      currIndex: number,
      nbDays = 1,
      nbAdultsOverride?: number,
      nbAdolescentsOverride?: number,
      nbChildrenOverride?: number,
      selectedHotelPensionType?: string,
    ) => {
      const benefitOriginalCurrency = benefit.currency;
      const { marginRate, prices } = computeBenefitPrices({
        benefit,
        selectedHotelPensionType,
        indexDay: currIndex,
        nbParticipants:
          nbAdultsOverride !== undefined
            ? nbAdultsOverride +
              (nbChildrenOverride ?? 0) +
              (nbAdolescentsOverride ?? 0)
            : nbAdults + nbChildren + nbAdolescents,
        circuitMarginRate: parsedCircuit.marginRate,
        nbChildren: nbChildrenOverride ?? nbChildren,
        nbAdolescents: nbAdolescentsOverride ?? nbAdolescents,
        nbDays,
        childrenAges:
          nbChildrenOverride != undefined ? undefined : childrenAges,
      });
      const rawPrice = prices.get(priceDateKey(date))?.minimumPrice;
      const benefitPrice = (rawPrice ?? Infinity) * (1 + marginRate / 100);
      return convertPrice(
        benefitPrice,
        benefitOriginalCurrency,
        circuitCurrency,
      );
    },
    [
      childrenAges,
      circuitCurrency,
      convertPrice,
      nbAdults,
      nbAdolescents,
      nbChildren,
      parsedCircuit.marginRate,
    ],
  );

  const form = useForm<EstimateFormData>({
    defaultValues: {
      selectedEstimateOptions: {},
      circuit: parsedCircuit,
      adults: new Array(nbAdults).fill({
        firstName: '',
        lastName: '',
      }),
      children: estimateChildrenBirthDates.map((birthDate) => ({
        firstName: '',
        lastName: '',
        birthDate,
      })),
      marginRate: 0,
    },
  });

  const { control, setValue, handleSubmit } = form;

  useEffect(() => {
    if (nbAdults > 0)
      setValue(
        'adults',
        new Array(nbAdults).fill({
          firstName: '',
          lastName: '',
        }),
      );
  }, [nbAdults, setValue]);

  useEffect(() => {
    setValue(
      'children',
      estimateChildrenBirthDates.map((birthDate) => ({
        firstName: '',
        lastName: '',
        birthDate,
      })),
    );
  }, [estimateChildrenBirthDates, setValue]);

  const { mutateAsync: updateDistributorCircuit } = useUpdateDistributorCircuit(
    editableCircuitId ?? '',
  );
  const { toastAsync } = useToast();

  const onSubmit: SubmitHandler<EstimateFormData> = useCallback(
    async (data) => {
      await toastAsync(
        updateDistributorCircuit({
          summary: data.circuit.summary,
          importantPoints: data.circuit.importantPoints,
          priceIncludes: data.circuit.priceIncludes,
          priceExcludes: data.circuit.priceExcludes,
          modificationsConditions: data.circuit.modificationsConditions,
          cancellationConditions: data.circuit.cancellationConditions,
          daysTitles: data.circuit.circuitDays.map(
            (day) => day.stepTitle ?? '',
          ),
          daysDescriptions: data.circuit.circuitDays.map(
            (day) => day.description ?? '',
          ),
          nbParticipants: nbAdults,
          nbChildren: nbChildren,
          title,
        }),
        { success: t('circuits.viewForm.saveSuccess') },
      );
    },
    [nbAdults, nbChildren, t, title, toastAsync, updateDistributorCircuit],
  );

  const [
    isCreateEditableCircuitModalOpen,
    setIsCreateEditableCircuitModalOpen,
  ] = useState(false);

  const requestSave = useCallback(() => {
    handleSubmit(onSubmit)();
  }, [handleSubmit, onSubmit]);

  return !isDetailsAndOptions ? (
    <div className="flex flex-col lg:flex-row-reverse gap-4 p-6">
      <div className="w-fit h-fit lg:sticky lg:top-20">
        <CircuitDetailsParticipantsCard
          minimumPrice={totalPrice}
          nbAdults={nbAdults}
          setNbAdults={setNbAdults}
          nbChildren={nbChildren}
          setNbChildren={setNbChildren}
          childrenBirthDates={childrenBirthDates}
          setChildrenBirthDates={setChildrenBirthDates}
          defaultNbParticipants={parsedCircuit.nbParticipants}
          selectedArrivalDate={selectedArrivalDate}
          onSelectOptionsButtonClick={onSelectOptionsButtonClick}
          currency={circuitCurrency}
          mainBtnLabel={t('circuits.editorial.details.viewOptions')}
          secondaryBtnLabel={editableCircuitId ? t('common.save') : undefined}
          onSecondaryBtnClick={editableCircuitId ? requestSave : undefined}
        />
      </div>
      <div className="flex flex-col gap-4 w-full">
        <CircuitDetailsImagesCard circuitId={circuitId} images={images} />
        <CircuitDetailsSummaryCard
          summary={getTranslationForCircuitValue('summary') ?? ''}
          title={t('circuits.editorial.details.summary')}
          edit={
            editableCircuitId
              ? {
                  control,
                  name: 'summary',
                  isViewMode: !canEditCircuit,
                }
              : undefined
          }
        />
        <CircuitDetailsSummaryCard
          summary={getTranslationForCircuitValue('importantPoints') ?? ''}
          title={t('circuits.editorial.details.importantPoints')}
          edit={
            editableCircuitId
              ? {
                  control,
                  name: 'importantPoints',
                  isViewMode: !canEditCircuit,
                }
              : undefined
          }
        />
        <div id="calendar">
          <CircuitDetailsPricesCalendarCard
            calculateMinimumPriceForDate={getDayPrice}
            nbParticipants={totalParticipants}
            onDayClick={(date) => {
              if (isDateSelectable(date) && setSelectedArrivalDate)
                setSelectedArrivalDate(date);
            }}
            defaultDate={selectedArrivalDate || startOfToday()}
            foldable={false}
            currency={circuitCurrency}
            lastPricedStartDate={lastPricedStartDate}
            onMonthChange={ensurePricedUntil}
          />
        </div>
        <CircuitDetailsItineraryCard
          itinerary={daysItinerary}
          control={editableCircuitId ? control : undefined}
        />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <CircuitDetailsSummaryCard
            title={t('circuits.editorial.details.priceIncludes')}
            summary={getTranslationForCircuitValue('priceIncludes') ?? ''}
            textBrand
            edit={
              editableCircuitId
                ? {
                    control,
                    name: 'priceIncludes',
                    isViewMode: !canEditCircuit,
                  }
                : undefined
            }
          />
          <CircuitDetailsSummaryCard
            title={t('circuits.editorial.details.priceExcludes')}
            summary={getTranslationForCircuitValue('priceExcludes') ?? ''}
            textBrand
            edit={
              editableCircuitId
                ? {
                    control,
                    name: 'priceExcludes',
                    isViewMode: !canEditCircuit,
                  }
                : undefined
            }
          />
          <CircuitDetailsSummaryCard
            title={t('circuits.editorial.details.modificationConditions')}
            summary={
              getTranslationForCircuitValue('modificationConditions') ?? ''
            }
            textBrand
            edit={
              editableCircuitId
                ? {
                    control,
                    name: 'modificationsConditions',
                    isViewMode: !canEditCircuit,
                  }
                : undefined
            }
          />
          <CircuitDetailsSummaryCard
            title={t('circuits.editorial.details.cancellationConditions')}
            summary={
              getTranslationForCircuitValue('cancellationConditions') ?? ''
            }
            textBrand
            edit={
              editableCircuitId
                ? {
                    control,
                    name: 'cancellationConditions',
                    isViewMode: !canEditCircuit,
                  }
                : undefined
            }
          />
        </div>
      </div>
    </div>
  ) : (
    selectedArrivalDate && (
      <FormProvider {...form}>
        <EstimateDetailsAndOptions
          estimateTitle={parsedCircuit.name ?? undefined}
          circuit={parsedCircuitWithMultiDayBenefitTracking}
          arrivalDate={selectedArrivalDate}
          adults={nbAdults}
          children={nbChildren}
          totalPrice={
            pricesByStartDate.get(selectedArrivalDate.toISOString()) ??
            findMinimumPriceForDate(selectedArrivalDate)
          }
          control={control}
          selectedOptions={resolvedSelectedOptions}
          setSelectedOptions={resolvedSetSelectedOptions}
          estimateMarginRate={0}
          computeOptionalBenefitPrice={computeOptionalBenefitPrice}
          status={EstimateStatus.PENDING}
          shouldHideInternationalFlights
          goToLastStep={() => {}}
          saveEstimate={() => {}}
          sendReservationRequest={() => {}}
          currency={circuitCurrency}
          optionsCurrency={circuitCurrency}
          isCircuitPage
          publishCircuit={publishCircuit}
          errorsLength={errorsLength}
          isValid={isValid}
          shouldHideCustomParagraph
          secondaryBtnText={
            canCreateEditableCircuitFromCircuit
              ? t('estimates.duplicateCircuitMessage')
              : editableCircuitId
                ? t('common.save')
                : undefined
          }
          onSecondaryClick={
            canCreateEditableCircuitFromCircuit
              ? () => setIsCreateEditableCircuitModalOpen(true)
              : editableCircuitId
                ? requestSave
                : undefined
          }
          language={language}
          isEditableCircuit={editableCircuitId !== undefined}
        />
        {isCreateEditableCircuitModalOpen && (
          <CreateEditableCircuitModal
            closeModal={() => setIsCreateEditableCircuitModalOpen(false)}
            circuitId={circuitId}
          />
        )}
      </FormProvider>
    )
  );
};
